import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  Line, ComposedChart, Cell 
} from 'recharts';
import api from '../services/api';
import toast from 'react-hot-toast';

const QuotationsPage = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1
  });

  const [filterDate, setFilterDate] = useState(''); // Default to 'All' (empty)
  const [filterStatus, setFilterStatus] = useState(''); // '', 'invoiced', 'pending'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isProcessing, setIsProcessing] = useState(null);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' o 'monthly'
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditGaps, setAuditGaps] = useState([]);
  const [auditing, setAuditing] = useState(false);
  const [investigating, setInvestigating] = useState({});

  const auditSequence = async () => {
    setAuditing(true);
    setShowAuditModal(true);
    try {
      const response = await api.get('/quotations/sequence/audit?lastN=500');
      if (response.data.success) {
        setAuditGaps(response.data.data.gaps || []);
      }
    } catch (error) {
      // toast.error ya lo maneja el interceptor de api.js
    } finally {
      setAuditing(false);
    }
  };

  const investigateGap = async (gap) => {
    setInvestigating(prev => ({ ...prev, [gap.number]: true }));
    try {
      const response = await api.get(
        `/quotations/sequence/investigate/${gap.number}?date=${gap.probable_date || ''}`
      );
      
      if (response.data.success && response.data.found) {
        toast.success(`¡Encontrado en SIIGO! Status: ${response.data.data.status}`);
        // Actualizar el gap localmente con la info encontrada
        setAuditGaps(prev => prev.map(g => 
          g.number === gap.number ? { ...g, found: true, details: response.data.data } : g
        ));
      } else {
        toast.info(response.data.message || 'No se encontró en SIIGO');
        setAuditGaps(prev => prev.map(g => 
          g.number === gap.number ? { ...g, found: false, investigated: true } : g
        ));
      }
    } catch (error) {
      // toast.error ya lo maneja el interceptor de api.js
    } finally {
      setInvestigating(prev => ({ ...prev, [gap.number]: false }));
    }
  };

  useEffect(() => {
    fetchStats();
    fetchDetailedStats();
  }, []);

  const fetchDetailedStats = async () => {
    try {
      const response = await api.get('/quotations/detailed-stats');
      if (response.data.success) {
        setChartData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching detailed stats:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/quotations/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchQuotations(1); // Siempre resetear a la página 1 al cambiar filtros
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filterDate, filterStatus, searchTerm]);

  // Auto-sync on first load only
  useEffect(() => {
    syncQuotations(true);
  }, []);

  const fetchQuotations = async (page = 1) => {
    try {
      setLoading(true);
      // Si filterDate está vacío (Ver Todas), el backend traerá todo
      const response = await api.get(`/quotations?page=${page}&limit=${pagination.limit}&date=${filterDate}&status=${filterStatus}&q=${searchTerm}`);
      if (response.data.success) {
        setQuotations(response.data.data);
        if (response.data.pagination) {
          setPagination(prev => ({ ...prev, ...response.data.pagination }));
        }
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      toast.error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const syncQuotations = async (silent = false) => {
    try {
      setSyncing(true);
      if (!silent) toast('Sincronizando cotizaciones de los últimos 45 días...', { icon: '🔄' });

      // Sincronizar los últimos 45 días para asegurar trazabilidad completa
      const response = await api.post('/quotations/sync', {
        start_date: new Date(new Date().setDate(new Date().getDate() - 45)).toISOString().split('T')[0]
      });

      if (response.data.success) {
        if (!silent) toast.success(`Sincronización completada. ${response.data.data.synced} cotizaciones procesadas.`);
        fetchQuotations(pagination.page);
        fetchStats();
      } else {
        if (!silent) toast.error('Error en la sincronización');
      }
    } catch (error) {
      console.error('Error syncing quotations:', error);
      if (!silent) toast.error('Error al sincronizar cotizaciones');
    } finally {
      setSyncing(false);
    }
  };

  const convertToInvoice = async (quotation) => {
    const confirm = window.confirm(`¿Estás seguro de que deseas convertir la cotización ${quotation.quotation_number || quotation.siigo_quotation_number} en una FACTURA de SIIGO?`);
    if (!confirm) return;

    try {
      setIsProcessing(quotation.id);
      toast.loading('Generando factura en SIIGO...', { id: 'invoice-toast' });

      const response = await api.post('/quotations/create-invoice', {
        quotationId: quotation.id,
        document_type: 'FV-1'
      });

      if (response.data.success) {
        toast.success(`Factura ${response.data.data.siigo_invoice_number} creada exitosamente`, { id: 'invoice-toast' });
        fetchQuotations(pagination.page);
        fetchStats();
      } else {
        toast.error(`Error: ${response.data.message}`, { id: 'invoice-toast' });
      }
    } catch (error) {
      console.error('Error converting to invoice:', error);
      const msg = error.response?.data?.message || 'Error al conectar con SIIGO';
      toast.error(`Error: ${msg}`, { id: 'invoice-toast' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const setYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setFilterDate(yesterday.toISOString().split('T')[0]);
  };

  const setToday = () => {
    setFilterDate(new Date().toISOString().split('T')[0]);
  };

  const setAll = () => {
    setFilterDate('');
  };

  // Ordenamiento Local (ya filtrado por backend)
  const filteredQuotations = [...quotations]
    .sort((a, b) => {
      const numA = parseInt((a.quotation_number || a.siigo_quotation_number || '0').toString().replace(/\D/g, '')) || 0;
      const numB = parseInt((b.quotation_number || b.siigo_quotation_number || '0').toString().replace(/\D/g, '')) || 0;
      return sortOrder === 'desc' ? numB - numA : numA - numB;
    });

  return (
    <div className="p-6">
      {/* Panel de Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 font-bold uppercase">Hoy: Creadas</p>
          <p className="text-2xl font-black text-blue-900">{stats?.today_total || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-green-500">
          <p className="text-xs text-gray-500 font-bold uppercase">Hoy: Facturadas</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-black text-green-900">{stats?.today_invoiced || 0}</p>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
              {stats?.today_total > 0 ? Math.round((stats.today_invoiced / stats.today_total) * 100) : 0}%
            </span>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-purple-500">
          <p className="text-xs text-gray-500 font-bold uppercase">Mes: Creadas</p>
          <p className="text-2xl font-black text-purple-900">{stats?.month_total || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-orange-500">
          <p className="text-xs text-gray-500 font-bold uppercase">Mes: Conversión</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-black text-orange-900">
              {stats?.month_total > 0 ? Math.round((stats.month_invoiced / stats.month_total) * 100) : 0}%
            </p>
            <span className="text-xs text-gray-400 font-medium">
              ({stats?.month_invoiced || 0} de {stats?.month_total || 0})
            </span>
          </div>
        </div>
      </div>

      {/* Histograma de Conversión */}
      <div className="bg-white p-6 rounded-xl shadow-sm mb-6 border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Efectividad de Conversión</h2>
            <p className="text-xs text-gray-500 font-medium">Cotizaciones vs. Facturadas con % de éxito</p>
          </div>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button 
              onClick={() => setViewMode('daily')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'daily' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Día a Día
            </button>
            <button 
              onClick={() => setViewMode('monthly')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${viewMode === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Mes a Mes
            </button>
          </div>
        </div>

        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={viewMode === 'daily' ? chartData.daily : chartData.monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis 
                dataKey="label" 
                axisLine={false} 
                tickLine={false} 
                tick={{fontSize: 10, fontWeight: 'bold', fill: '#6b7280'}}
                dy={10}
              />
              <YAxis 
                yAxisId="left"
                axisLine={false} 
                tickLine={false} 
                tick={{fontSize: 10, fontWeight: 'bold', fill: '#6b7280'}}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                axisLine={false} 
                tickLine={false} 
                unit="%"
                tick={{fontSize: 10, fontWeight: 'bold', fill: '#f59e0b'}}
              />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                cursor={{ fill: '#f9fafb' }}
              />
              <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '20px', fontSize: '12px', fontWeight: 'bold' }} />
              
              <Bar yAxisId="left" dataKey="invoiced" name="✅ Facturadas" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} barSize={20} />
              <Bar yAxisId="left" dataKey="pending" name="⏳ Pendientes" stackId="a" fill="#e5e7eb" radius={[4, 4, 0, 0]} barSize={20} />
              
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="conversion_rate" 
                name="% Conversión" 
                stroke="#f59e0b" 
                strokeWidth={3}
                dot={{ r: 4, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Cotizaciones</h1>
          <p className="text-gray-500 text-sm">Gestiona y factura tus cotizaciones de SIIGO</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Buscador */}
          <div className="relative flex-grow md:flex-grow-0">
            <input
              type="text"
              placeholder="Buscar por número o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border-2 border-blue-200 rounded-lg px-4 py-2 text-sm font-bold focus:ring-2 focus:ring-blue-500 outline-none bg-white text-blue-800 shadow-sm"
          >
            <option value="">📂 Todos los Estados</option>
            <option value="pending">⏳ Por Facturar (Pendientes)</option>
            <option value="invoiced">✅ Facturadas</option>
          </select>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={setYesterday}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${filterDate === new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().split('T')[0] ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Ayer
            </button>
            <button
              onClick={setToday}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${filterDate === new Date().toISOString().split('T')[0] ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Hoy
            </button>
            <button
              onClick={setAll}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${filterDate === '' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Ver Todas
            </button>
          </div>

          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />

          <button
            onClick={auditSequence}
            className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium shadow-sm transition-all flex items-center gap-2 active:transform active:scale-95"
          >
            🔍 Auditar Secuencia
          </button>

          <button
            onClick={() => syncQuotations(false)}
            disabled={syncing}
            className={`px-4 py-2 rounded-lg text-white font-medium shadow-sm transition-all flex items-center gap-2 ${syncing ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 active:transform active:scale-95'
              }`}
          >
            {syncing ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Sincronizando...
              </>
            ) : (
              <>🔄 Sincronizar SIIGO</>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                <th 
                  className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-600 flex items-center gap-1"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  Cotización
                  <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Valor Total</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Operaciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span>Cargando cotizaciones...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredQuotations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-4xl">📄</span>
                      <p>No se encontraron resultados. Prueba dándole a "Sincronizar SIIGO".</p>
                      <button onClick={() => { setSearchTerm(''); setFilterDate(''); }} className="text-blue-600 hover:underline text-sm font-medium mt-2">
                        Limpiar filtros
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredQuotations.map((quotation) => (
                  <tr key={quotation.id} className="hover:bg-blue-50 transition-colors group">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(quotation.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900">
                          {quotation.quotation_number || quotation.siigo_quotation_number || 'N/A'}
                        </span>
                        {quotation.siigo_invoice_number && (
                          <span className="text-[10px] text-purple-600 font-bold uppercase tracking-tighter">
                            Factura: {quotation.siigo_invoice_number}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">{quotation.customer_name || 'Consumidor Final'}</div>
                      <div className="text-xs text-gray-500">{quotation.commercial_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                      ${Number(quotation.total_amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {quotation.status === 'invoiced' ? (
                        <span className="px-3 py-1 inline-flex text-[10px] leading-5 font-bold rounded-full bg-purple-100 text-purple-800 border border-purple-200 uppercase tracking-wider">
                          Facturada
                        </span>
                      ) : quotation.status === 'completed' ? (
                        <span className="px-3 py-1 inline-flex text-[10px] leading-5 font-bold rounded-full bg-green-100 text-green-800 border border-green-200 uppercase tracking-wider">
                          Vigente
                        </span>
                      ) : (
                        <span className="px-3 py-1 inline-flex text-[10px] leading-5 font-bold rounded-full bg-gray-100 text-gray-600 border border-gray-200 uppercase tracking-wider">
                          {quotation.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end items-center gap-3">
                        {quotation.status !== 'invoiced' && (
                          <button
                            onClick={() => convertToInvoice(quotation)}
                            disabled={isProcessing === quotation.id}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all shadow-sm ${isProcessing === quotation.id
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-green-600 text-white hover:bg-green-700 active:scale-95'
                              }`}
                          >
                            {isProcessing === quotation.id ? 'Facturando...' : '🚀 Facturar'}
                          </button>
                        )}

                        <div className="flex items-center gap-2">
                          {quotation.siigo_quotation_url && (
                            <a
                              href={quotation.siigo_quotation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 bg-gray-100 rounded-md text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-all title='Ver Cotización'"
                              title="Ver Cotización"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </a>
                          )}

                          {quotation.siigo_invoice_url && (
                            <a
                              href={quotation.siigo_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 bg-purple-100 rounded-md text-purple-600 hover:bg-purple-200 transition-all"
                              title="Ver Factura"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-100">
          <div className="flex-1 flex justify-between items-center">
            <span className="text-xs text-gray-500 font-medium">
              Mostrando <span className="text-gray-900">{filteredQuotations.length}</span> cotizaciones
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className={`px-3 py-1 rounded-md text-xs font-semibold border ${pagination.page === 1 ? 'bg-gray-50 text-gray-300 border-gray-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 active:scale-95'
                  }`}
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 rounded-md text-xs font-semibold border ${pagination.page >= pagination.totalPages ? 'bg-gray-50 text-gray-300 border-gray-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 active:scale-95'
                  }`}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Modal de Auditoría de Secuencia */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-purple-50">
              <div>
                <h3 className="text-xl font-black text-purple-900">Auditoría de Secuencia (SIIGO)</h3>
                <p className="text-sm text-purple-600 font-bold">Detección de saltos en consecutivos C-1-XXXXX</p>
              </div>
              <button 
                onClick={() => setShowAuditModal(false)}
                className="p-2 hover:bg-purple-100 rounded-full transition-colors text-purple-400 hover:text-purple-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {auditing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                  <p className="text-gray-500 font-bold">Escaneando base de datos local...</p>
                </div>
              ) : auditGaps.length === 0 ? (
                <div className="text-center py-12">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-800">¡Secuencia Perfecta!</h4>
                  <p className="text-gray-500">No se encontraron saltos en los últimos 500 registros.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 mb-6">
                    <p className="text-sm text-amber-700 font-bold flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                      Se encontraron {auditGaps.length} saltos en la secuencia local.
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Esto puede deberse a cotizaciones anuladas en SIIGO o fallos en la sincronización automática.
                    </p>
                  </div>

                  <div className="divide-y divide-gray-100 border rounded-xl">
                    {auditGaps.map((gap) => (
                      <div key={gap.number} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                        <div>
                          <p className="font-black text-gray-800">{gap.full_number}</p>
                          <p className="text-xs text-gray-400 font-bold">Fecha Probable: {gap.probable_date || 'N/D'}</p>
                          {gap.found && (
                            <div className="mt-2 text-xs">
                              <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-black uppercase">
                                Encontrado: {gap.details?.status || 'Active'}
                              </span>
                              <p className="mt-1 text-gray-500">Cliente: {gap.details?.customer?.name || gap.details?.customer?.identification}</p>
                              <p className="text-gray-500">Total: ${new Intl.NumberFormat().format(gap.details?.total || 0)}</p>
                            </div>
                          )}
                          {gap.investigated && !gap.found && (
                            <span className="mt-2 inline-block bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-black uppercase">
                              No existe en SIIGO
                            </span>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {!gap.found && (
                            <button
                              onClick={() => investigateGap(gap)}
                              disabled={investigating[gap.number]}
                              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                investigating[gap.number] 
                                ? 'bg-gray-100 text-gray-400' 
                                : 'bg-white border border-purple-200 text-purple-600 hover:bg-purple-50'
                              }`}
                            >
                              {investigating[gap.number] ? 'Investigando...' : 'Investigar en SIIGO'}
                            </button>
                          )}
                          {gap.found && (
                            <button
                              onClick={() => {
                                setShowAuditModal(false);
                                syncQuotations(true); // Sincronización forzada
                              }}
                              className="px-4 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700"
                            >
                              Sincronizar Ahora
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button 
                onClick={() => setShowAuditModal(false)}
                className="px-6 py-2 text-sm font-bold text-gray-600 hover:text-gray-800"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationsPage;
